import { useEffect, useRef, useState } from "react";
import DialogAlertAction from "components/DialogAlertAction/DialogAlertAction";
import CustomButton from "components/CustomButton/CustomButton";
import CustomDatePicker from "components/CustomDatePicker/CustomDatePicker";
import CustomFileInput from "components/CustomFileInput/CustomFileInput";
import CustomSelect from "components/CustomSelect/CustomSelect";
import CustomTextInput from "components/CustomTextInput/CustomTextInput";
import HandlerCheckbox from "components/CustomCheckbox/CustomCheckbox";
import ErrorMessage from "components/ErrorMessage/ErrorMessage";
import ErrorIcon from "icons/ErrorIcon";
import {
	createEmptySignatureForm,
	INITIAL_FORM_ERRORS,
} from "../../utils/constants";
import {
	isInstitutionalSignature,
	mapSignatureToForm,
	normalizeCampusIdsBySignatureType,
} from "../../utils/helpers";
import { validateEventSignatureForm } from "../../utils/validations";
import {
	CampusSelectionCard,
	CompactGrid,
	DialogLead,
	DialogFormContent,
	DualSectionGrid,
	FooterActions,
	FileInfoCard,
	FormSection,
	ErrorAlertBlock,
	SectionHeader,
	StackFields,
	Subsection,
	SubsectionHeader,
	UploadFieldWrap,
} from "../../styles";

const EventSignatureFormDialog = ({
	show,
	toggleShow,
	currentItem,
	allCampus,
	signatureTypeOptions,
	onSubmit,
	onPreviewFile,
	submitError,
}) => {
	const [form, setForm] = useState(createEmptySignatureForm());
	const [errors, setErrors] = useState(INITIAL_FORM_ERRORS);
	const formContentRef = useRef(null);
	const fieldRefs = useRef({});

	useEffect(() => {
		if (!show) return;
		setForm(mapSignatureToForm(currentItem));
		setErrors(INITIAL_FORM_ERRORS);
	}, [show, currentItem]);

	useEffect(() => {
		if (!submitError || !formContentRef.current) return;
		formContentRef.current.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	}, [submitError]);

	const scrollToFieldError = fieldName => {
		const fieldContainer = fieldRefs.current[fieldName];
		if (!fieldContainer) return;

		fieldContainer.scrollIntoView({
			behavior: "smooth",
			block: "center",
		});

		const focusableElement = fieldContainer.querySelector(
			"input, select, textarea, button, [tabindex]"
		);
		if (focusableElement && typeof focusableElement.focus === "function") {
			focusableElement.focus({ preventScroll: true });
		}
	};

	const campusCheckboxProps = allCampus.map(({ id, name }) => ({
		value: id,
		label: name,
		checked: form.campusIds.includes(id),
	}));

	const changeField = (field, value) => {
		setForm(prev => ({
			...prev,
			[field]: value,
			...(field === "signatureTypeCode"
				? {
						campusIds: normalizeCampusIdsBySignatureType(
							value,
							prev.campusIds
						),
				  }
				: {}),
		}));
		setErrors(prev => ({
			...prev,
			[field]: false,
		}));
		if (field === "signatureTypeCode") {
			setErrors(prev => ({
				...prev,
				campusIds: false,
			}));
		}
	};

	const toggleCampus = campusId => {
		changeField(
			"campusIds",
			institutionalType
				? form.campusIds.includes(campusId)
					? []
					: [campusId]
				: form.campusIds.includes(campusId)
					? form.campusIds.filter(id => id !== campusId)
					: [...form.campusIds, campusId]
		);
	};

	const submit = async () => {
		const validation = validateEventSignatureForm(form);
		setErrors(validation.errors);
		if (!validation.isValid) {
			const firstErrorField = Object.keys(validation.errors).find(
				key => validation.errors[key]
			);
			if (firstErrorField) {
				requestAnimationFrame(() => {
					scrollToFieldError(firstErrorField);
				});
			}
			return;
		}
		await onSubmit(form);
	};

	const institutionalType = isInstitutionalSignature(form.signatureTypeCode);

	return (
		<DialogAlertAction
			show={show}
			toggleShow={toggleShow}
			noImage
			title={currentItem ? "Editar firma de evento" : "Registrar firma de evento"}
			width="min(960px, calc(100vw - 40px))"
			minWidth="0"
			widthContent="100%"
			spacingContainer="24px"
			contentComponent={
				<DialogFormContent ref={formContentRef}>
					<DialogLead>
						<h4>{currentItem ? "Editar configuración" : "Nueva firma"}</h4>
						<p>Define tipo, campus, vigencia y archivo de firma.</p>
					</DialogLead>
					{submitError ? (
						<ErrorAlertBlock>
							<ErrorIcon color="var(--red-3)" />
							<p>{submitError}</p>
						</ErrorAlertBlock>
					) : null}
					<FormSection>
						<SectionHeader>
							<h4>Datos generales</h4>
						</SectionHeader>
						<CompactGrid>
							<div ref={element => { fieldRefs.current.signatureTypeCode = element; }}>
								<CustomSelect
									label="Tipo de firma"
									value={form.signatureTypeCode}
									onChange={value => changeField("signatureTypeCode", value)}
									optionsProps={signatureTypeOptions || []}
									defaultOptionText="Selecciona un tipo"
									error={errors.signatureTypeCode}
									errorText="Selecciona un tipo de firma"
									width={{
										container: "100%",
										containerSelect: "100%",
									}}
									required
								/>
							</div>
							<div ref={element => { fieldRefs.current.displayName = element; }}>
								<CustomTextInput
									label={institutionalType ? "Nombre visible" : "Nombre visible / nombre completo"}
									value={form.displayName}
									onChange={value => changeField("displayName", value)}
									placeholder="Escribe el nombre que se mostrará"
									error={errors.displayName}
									errorText="Ingresa un nombre visible"
									width="100%"
									required
								/>
							</div>
							<div>
								<CustomTextInput
									label="Código UTP (opcional)"
									value={form.utpCode}
									onChange={value => changeField("utpCode", value)}
									placeholder="Opcional"
									width="100%"
								/>
							</div>
							<HandlerCheckbox
								variant="switch"
								description="Estado inicial"
								onChange={() => changeField("isActive", !form.isActive)}
								checkboxProps={[
									{
										value: "active",
										label: form.isActive ? "Activo" : "Inactivo",
										checked: form.isActive,
									},
								]}
							/>
						</CompactGrid>
					</FormSection>
					<FormSection>
						<SectionHeader>
							<h4>Campus asociados</h4>
							<p>
								{institutionalType
									? "Selecciona un campus."
									: "Puedes seleccionar uno o varios campus."}
							</p>
						</SectionHeader>
						<CampusSelectionCard ref={element => { fieldRefs.current.campusIds = element; }}>
							<HandlerCheckbox
								gap="12px"
								padding="0"
								size="small"
								labelColor="var(--dark-4)"
								onChange={toggleCampus}
								checkboxProps={campusCheckboxProps}
								error={errors.campusIds}
								errorText={
									institutionalType
										? "Selecciona exactamente un campus para esta firma institucional"
										: "Selecciona al menos un campus"
								}
							/>
						</CampusSelectionCard>
					</FormSection>
					<FormSection>
						<SectionHeader>
							<h4>Archivo y vigencia</h4>
						</SectionHeader>
						<DualSectionGrid>
							<Subsection>
								<SubsectionHeader>
									<h5>Archivo</h5>
									<p>Solo se admite PNG.</p>
								</SubsectionHeader>
								{currentItem?.signatureFile?.name && !form.signatureFile?.base64 && (
									<FileInfoCard>
										<span>Archivo actual</span>
										<strong>{currentItem.signatureFile.name}</strong>
										{currentItem.signatureFile?.path ? (
											<CustomButton
												variant="secondary"
												size="small"
												text="Ver archivo"
												onClick={() => onPreviewFile(currentItem)}
											/>
										) : null}
									</FileInfoCard>
								)}
								<UploadFieldWrap ref={element => { fieldRefs.current.signatureFile = element; }}>
									<CustomFileInput
										fileTypes={["png"]}
										file={form.signatureFile}
										onChange={value => changeField("signatureFile", value)}
										error={errors.signatureFile}
										errorText="Sube un archivo PNG de firma"
										maxSizeMB={2}
									/>
								</UploadFieldWrap>
							</Subsection>
							<Subsection>
								<SubsectionHeader>
									<h5>Vigencia</h5>
									<p>Indica el periodo de uso o déjala sin fecha final.</p>
								</SubsectionHeader>
								<StackFields>
									<div ref={element => { fieldRefs.current.validFrom = element; }}>
										<CustomDatePicker
											label="Inicio de vigencia"
											value={form.validFrom}
											onChange={value => changeField("validFrom", value)}
											period={{
												min: [2024, 0, 1],
												max: [3000, 0, 1],
											}}
											error={errors.validFrom}
											errorText="Selecciona la fecha de inicio"
											required
											width="100%"
											popupMode="contained"
										/>
									</div>
									<div ref={element => { fieldRefs.current.validTo = element; }}>
										<CustomDatePicker
											label="Fin de vigencia"
											value={form.validTo}
											onChange={value => changeField("validTo", value)}
											period={{
												min: [2024, 0, 1],
												max: [3000, 0, 1],
											}}
											disabled={form.isIndefiniteValidity}
											error={errors.validTo}
											errorText="Selecciona una fecha fin válida"
											width="100%"
											popupMode="contained"
										/>
									</div>
									<HandlerCheckbox
										variant="switch"
										description="Vigencia indeterminada"
										onChange={() =>
											changeField(
												"isIndefiniteValidity",
												!form.isIndefiniteValidity
											)
										}
										checkboxProps={[
											{
												value: "indefinite-validity",
												label: form.isIndefiniteValidity ? "Sí" : "No",
												checked: form.isIndefiniteValidity,
											},
										]}
									/>
								</StackFields>
							</Subsection>
						</DualSectionGrid>
					</FormSection>
					<ErrorMessage
						error={
							Boolean(errors.validFrom) ||
							Boolean(errors.validTo)
						}
						text="Si la vigencia no es indeterminada, la fecha fin debe ser mayor o igual a la fecha inicio."
					/>
					<FooterActions>
						<CustomButton
							variant="secondary"
							size="small"
							text="Cancelar"
							onClick={() => toggleShow(false)}
						/>
						<CustomButton
							size="small"
							text={currentItem ? "Guardar cambios" : "Registrar firma"}
							onClick={submit}
						/>
					</FooterActions>
				</DialogFormContent>
			}
		/>
	);
};

export default EventSignatureFormDialog;
